import React, { useEffect } from "react";

import cx from "classnames";
import { select } from "d3-selection";

import {
  StatusBarDataItem,
  StatusBarItemType,
  totalData,
  calculatePercents,
} from "./helpers";
import styles from "./StatusBarChart.scss";

const xInitialPosition = -200;
const yInitialPosition = -200;

interface ChartTooltipProps {
  data: StatusBarDataItem[];
  selectedTag: StatusBarItemType;
  showTooltip: boolean;
  svgRef: React.RefObject<SVGSVGElement>;
  tooltipContent: React.FC<{
    data: StatusBarDataItem[];
    tooltips?: Record<StatusBarItemType, { status?: string; text?: string }>;
  }>;
  tooltips?: Record<StatusBarItemType, { status?: string; text?: string }>;
}

export default function ChartTooltip({
  data,
  selectedTag,
  showTooltip,
  svgRef,
  tooltipContent,
  tooltips,
}: ChartTooltipProps) {
  const TooltipContentComponent = tooltipContent;
  const total = totalData(data);
  const percents = calculatePercents(data, total);

  const tooltipData = showTooltip
    ? percents.filter((f) => f.tag === selectedTag)
    : percents;

  const width = 160;
  const foreignObjectWidth = width;

  const tooltipContainerClass = cx(
    "tooltip-container",
    styles["chart-tooltip-container"]
  );

  const tooltipBoxClass = cx("tooltip-box", styles["chart-tooltip-box"]);

  useEffect(() => {
    const svgElement = select(svgRef.current);
    const gElement = svgElement.select(".chart-tooltip");
    const tooltipContainer = svgElement.select(".tooltip-container");
    const tooltipContainerProps = {
      height: parseInt(tooltipContainer.style("height"), 10),
    };

    const foreignObjectHeight = tooltipContainerProps.height;
    const rectHeight = tooltipContainerProps.height;

    if (showTooltip) {
      gElement.attr("opacity", "1");
    } else {
      gElement.attr("opacity", "0").attr("transform", "translate(-500, -500)");
    }

    svgElement.select(".tooltip-box").attr("height", rectHeight);
    svgElement
      .select(".chart-tooltip-foreign-object")
      .attr("height", foreignObjectHeight);
  }, [tooltipData, showTooltip]);

  return (
    <>
      <g className="chart-tooltip" data-testid="chart-tooltip">
        <defs>
          <filter id="chart-tooltip-def">
            <feDropShadow
              className="chart-tooltip-filter-effect"
              dx={0}
              dy={0}
              stdDeviation={2}
            />
          </filter>
        </defs>
        <rect
          className={tooltipBoxClass}
          x={xInitialPosition}
          y={yInitialPosition}
          width={width}
          height={86}
          filter="url(#chart-tooltip-def)"
        />
        <foreignObject
          className="chart-tooltip-foreign-object"
          x={xInitialPosition}
          y={yInitialPosition}
          height={92}
          width={foreignObjectWidth}
        >
          <div className={tooltipContainerClass}>
            <TooltipContentComponent data={tooltipData} tooltips={tooltips} />
          </div>
        </foreignObject>
      </g>
    </>
  );
}

export const tooltipCoords = (
  svgRef: React.RefObject<SVGSVGElement>,
  [xPoint, yPoint]: [number, number]
): [number, number] => {
  const xOffSet = 20;
  const svgElement = select(svgRef.current);
  const svgHeight = parseInt(svgElement.style("height"), 10);
  const svgWidth = parseInt(svgElement.style("width"), 10);
  const svgX = parseInt(svgElement.style("x"), 10);
  const svgMiddleX = svgX + svgWidth / 2;
  const tooltipElem = select(".tooltip-box");
  const tooltipHeight = parseInt(tooltipElem.attr("height"), 10);
  const tooltipWidth = parseInt(tooltipElem.attr("width"), 10);

  let tooltipX = xPoint - xInitialPosition + xOffSet;
  let tooltipY = yPoint - yInitialPosition - tooltipHeight;

  if (xPoint > svgMiddleX) {
    tooltipX = xPoint - xInitialPosition - xOffSet - tooltipWidth;
  }

  if (tooltipY + tooltipHeight > svgHeight) {
    tooltipY = yPoint - yInitialPosition - tooltipHeight;
  }

  return [tooltipX, tooltipY];
};
